import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserAuth } from '../contexts/UserAuthContext';
import { isNativeApp } from '../lib/nativeBridge';
import { journey, journeyResume } from '../lib/journey';
/**
 * Handles the redirect back from Emergent Google OAuth.
 *   URL shape: /profile#session_id=<token>
 *   1. Synchronously processed (caller wraps it before Routes).
 *   2. Exchanges session_id → our session_token via /api/auth/google/session.
 *   3. Waits until the user record is set, THEN strips the hash and navigates
 *      to /profile. That avoids a flash of an unauth'd ProfilePage between
 *      the spinner and the signed-in view.
 *   4. Surfaces a clear error UI with a Retry button if the exchange fails.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exchangeSession, exchangeGoogleCode, startGoogleLogin } = useUserAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  // iter879 · A sign-in started in the iOS app that finished in Safari.
  const [backToApp, setBackToApp] = useState(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    // iter344 · Native iOS white-labeled Google OAuth lands here with
    // ?code=…&state=… (Universal Link only intercepts /auth/callback,
    // not /auth/google, so we dispatch by param shape). Delegates to
    // exchangeGoogleCode → /auth/google/exchange — same backend endpoint
    // the web direct flow uses.
    //
    // iter450 · This used to pipe the exchange's session_token BACK into
    // exchangeSession() — which treats it as an Emergent session_id and
    // always 401s. Result: every Safari-fallback Google login showed
    // "Sign-in failed. Try again." even though the session was created.
    // exchangeGoogleCode() consumes the exchange response directly (sets
    // user + persists the token to Capacitor Preferences) so the FIRST
    // attempt now completes.
    const params = new URLSearchParams(location.search || window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code && state) {
      (async () => {
        try {
          const savedState = window.sessionStorage.getItem('flyt_oauth_state');
          if (savedState && savedState !== state) throw new Error('OAuth state mismatch');
          const publicOrigin = (process.env.REACT_APP_PUBLIC_ORIGIN
            || process.env.REACT_APP_BACKEND_URL
            || window.location.origin).replace(/\/+$/, '');
          const u = await exchangeGoogleCode(code, `${publicOrigin}/auth/callback`, state);
          if (!u) throw new Error('No user returned');
          try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
          // iter880 · App Health — an app sign-in that finished in Safari
          // is reported against the iPhone attempt (id = OAuth state).
          if (state.startsWith('app_') && !isNativeApp()) {
            journey('login_google', 'browser_done', { attempt: state, platform: 'ios' });
          }
          // iter879 · `app_` state = this sign-in was started from the iOS
          // app but finished here in Safari. The app is already polling
          // /auth/handoff for this exact session, so don't drop the athlete
          // into the website — send them back to the app.
          // iter884 · No automatic flythouse:// jump — inside the in-app
          // Google sheet that pops an "Open in FLYT?" prompt. The app's
          // handoff poll closes the sheet and signs in on its own (how it
          // worked before); the button covers the Safari-app case.
          if (state.startsWith('app_') && !isNativeApp()) {
            setBackToApp(true);
            return;
          }
          // iter765 · Jay (day before launch): this used to send brand-new
          // signups to the separate full-page /welcome picker (Athlete /
          // Agent / Brand / Program) while everyone else got PROMPTED by
          // the globally-mounted <PersonaWelcomeModal/> instead — two
          // different onboarding UIs offering two different persona
          // lists, depending purely on which sign-in method someone used.
          // That's what made the flow feel broken. Always land on /profile
          // now; PersonaWelcomeModal is mounted app-wide and shows itself
          // automatically for any signed-in user with no persona yet, so
          // this is the same single onboarding path for everyone.
          requestAnimationFrame(() => navigate('/profile', { replace: true }));
        } catch (e) {
          if (state.startsWith('app_')) {
            journey('login_google', 'fail', { attempt: state, platform: isNativeApp() ? undefined : 'ios',
              error: `Callback exchange: ${e?.response?.data?.detail || e?.message || 'failed'}`.slice(0, 300) });
          }
          console.error('Google direct exchange failed', e);
          setError(e?.response?.data?.detail || e?.message || 'Sign-in failed. Please try again.');
        }
      })();
      return;
    }
    const hash = location.hash || window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate('/profile', { replace: true }); return; }
    const sessionId = decodeURIComponent(match[1]);
    // iter880 · App Health — finish the Emergent-flow attempt.
    const sessionJourney = journeyResume('login_google', 'flyt_journey_google');
    (async () => {
      try {
        const u = await exchangeSession(sessionId);
        if (!u) throw new Error('No user returned');
        sessionJourney?.ok();
        // Strip the hash so refresh / back doesn't re-trigger the callback.
        try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch { /* ignore */ }
        // iter765 · See the comment in the Google-code branch above — this
        // used to send brand-new signups to the separate /welcome picker.
        // Always /profile now; PersonaWelcomeModal (mounted app-wide)
        // handles onboarding for every sign-in path the same way.
        requestAnimationFrame(() => navigate('/profile', { replace: true }));
      } catch (e) {
        sessionJourney?.fail(e);
        console.error('Auth exchange failed', e);
        setError(e?.response?.data?.detail || 'We couldn\'t complete sign-in. Please try again.');
      }
    })();
  }, [exchangeSession, exchangeGoogleCode, location.hash, navigate]);
  // Paint the document background black for the entire callback lifecycle so
  // any sub-frame gap during the redirect doesn't flash white.
  useEffect(() => {
    const prevHtml = document.documentElement.style.backgroundColor;
    const prevBody = document.body.style.backgroundColor;
    document.documentElement.style.backgroundColor = '#000';
    document.body.style.backgroundColor = '#000';
    return () => {
      document.documentElement.style.backgroundColor = prevHtml;
      document.body.style.backgroundColor = prevBody;
    };
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white px-6" data-testid="auth-callback" style={{ backgroundColor: '#000' }}>
      <div className="text-center max-w-md">
        {backToApp ? (
          <div data-testid="auth-back-to-app">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 text-xl">✓</div>
            <p className="mt-5 font-display text-2xl uppercase">You&rsquo;re signed in</p>
            <p className="mt-2 text-sm text-white/70">Head back to the FLYT app &mdash; it signs you in automatically.</p>
            <a
              href="flythouse://profile"
              className="mt-6 inline-block px-6 py-3 rounded-full bg-white text-black text-sm font-bold hover:bg-gray-100"
              data-testid="auth-open-app-btn"
            >
              Open the FLYT app
            </a>
            <p className="mt-4 text-[11px] text-white/40">Or switch back to FLYT from your recent apps.</p>
          </div>
        ) : !error ? (
          <>
            <div className="w-12 h-12 mx-auto rounded-full border-2 border-white/20 border-t-white animate-spin" />
            <p className="mt-5 text-sm uppercase tracking-widest text-white/80">Signing you in…</p>
            <p className="mt-2 text-[11px] text-white/40">Securing your session</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-300 text-xl">✕</div>
            <p className="mt-5 font-display text-2xl uppercase">Sign-in failed</p>
            <p className="mt-2 text-sm text-white/70">{error}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => startGoogleLogin('/profile')}
                className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold hover:bg-gray-100"
                data-testid="auth-retry-btn"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => navigate('/profile', { replace: true })}
                className="px-5 py-2.5 rounded-full border border-white/25 text-white text-sm font-bold hover:bg-white/10"
              >
                Go back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default AuthCallback;
